import {useEffect,useState} from 'react';
type Program={id:string;name:string;enabled:boolean;manualLocationCodes:string[]};
type ReferredCustomer={
  customerId:string;displayName:string;signupAt:string;
  firstServiceAt:string|null;firstService:string|null;completedFirstService:boolean;
};
type ReferralHistory={referrerName:string;referred:ReferredCustomer[];
  total:number;completedCount:number;hasMore:boolean;provenance:'pwa-records-only'};
type ReferralCode={code:string;version:number;active:boolean;programId:string;source:'GENERATED'|'STAFF_EDITED'};
type Grant={id:string;programId:string;programName:string;source:'MANUAL'|'MEMBERSHIP';
  sourceKey:string;referrerCustomerId:string|null;endedAt:string|null;
  verifiedCompletedReferrals:number};
export function CustomerReferralProgramsPanel({customerId,staffId,locationCode}:{
  customerId:string|null;staffId:string;locationCode:string}){
  const [resolvedCustomerId,setResolvedCustomerId]=useState<string|null>(customerId);
  const targetCustomerId=customerId??resolvedCustomerId;
  const programPath=targetCustomerId
    ?'/api/v1/staff/customers/'+encodeURIComponent(targetCustomerId)
    :'/api/v1/staff/team/'+encodeURIComponent(staffId);
  const [programs,setPrograms]=useState<Program[]>([]);
  const [grants,setGrants]=useState<Grant[]>([]);
  const [programId,setProgramId]=useState('');
  const [editing,setEditing]=useState(false);
  const [status,setStatus]=useState('');
  const [busy,setBusy]=useState(false);
  const [referralCode,setReferralCode]=useState<ReferralCode|null>(null);
  const [codeEditing,setCodeEditing]=useState(false);
  const [codeDraft,setCodeDraft]=useState('');
  const [codeSaving,setCodeSaving]=useState(false);
  const [history,setHistory]=useState<ReferralHistory|null>(null);
  const [historyError,setHistoryError]=useState('');
  const [historyOpen,setHistoryOpen]=useState(false);
  const refresh=async(signal?:AbortSignal,assignedCustomerId?:string)=>{
    const [response,people]=await Promise.all([
      fetch('/api/v1/staff/referral-programs?'+
        new URLSearchParams({location:locationCode}),{
          credentials:'same-origin',cache:'no-store',signal}),
      fetch((assignedCustomerId
        ?'/api/v1/staff/customers/'+encodeURIComponent(assignedCustomerId)
        :programPath)+'/referral-programs?'+
        new URLSearchParams({location:locationCode}),{
          credentials:'same-origin',cache:'no-store',signal}),
    ]);
    if(!response.ok||!people.ok)throw Error('Referral details unavailable.');
    const programsBody=await response.json() as {ok:boolean;data?:{programs:Program[]}};
    const grantsBody=await people.json() as {ok:boolean;data?:{
      grants:Grant[];referralCode?:ReferralCode|null;customerId?:string|null}};
    if(!programsBody.ok||!grantsBody.ok||
      !programsBody.data||!grantsBody.data)throw Error('Referral details unavailable.');
    if(!signal?.aborted){setPrograms(programsBody.data.programs);
      setGrants(grantsBody.data.grants);
      if(grantsBody.data.customerId)setResolvedCustomerId(grantsBody.data.customerId);
      setReferralCode(grantsBody.data.referralCode??null);}
  };
  useEffect(()=>{
    const controller=new AbortController();
    void refresh(controller.signal).catch(()=>{
      if(!controller.signal.aborted)setStatus('Referral details unavailable.');
    });
    return ()=>controller.abort();
  },[customerId,staffId,locationCode]);
  // Independently load the known PWA-referral records. Do not make an
  // unavailable optional history widget break program enrollments.
  useEffect(()=>{
    const controller=new AbortController();
    setHistory(null);setHistoryError('');setHistoryOpen(false);
    if(!targetCustomerId)return ()=>controller.abort();
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(targetCustomerId)+
      '/referrals?'+new URLSearchParams({location:locationCode,limit:'50'}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async response=>{
        if(!response.ok)throw Error('Referral history unavailable.');
        const data=await response.json() as {
          ok:boolean;data?:{history:ReferralHistory}};
        if(!data.ok||!data.data)throw Error('Referral history unavailable.');
        if(!controller.signal.aborted)setHistory(data.data.history);
      }).catch(()=>{
        if(!controller.signal.aborted)setHistoryError('Referral history unavailable.');
      });
    return ()=>controller.abort();
  },[targetCustomerId,locationCode]);
  const available=programs.filter(p=>p.enabled&&
    (!p.manualLocationCodes.length||p.manualLocationCodes.includes(locationCode)));
  const change=async(id:string,active:boolean)=>{
    if(busy)return;
    setBusy(true);setStatus('');
    try{
      const response=await fetch(programPath+'/referral-programs',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,
          programId:id,referrerCustomerId:null,
          active}),
      });
      const data=await response.json() as {ok:boolean;
        data?:{customerId?:string|null};error?:{code:string}};
      if(!response.ok||!data.ok)throw Error(data.error?.code??'Unable to update enrollment.');
      if(data.data?.customerId)setResolvedCustomerId(data.data.customerId);
      await refresh(undefined,data.data?.customerId??undefined);setProgramId('');
      setEditing(false);
      setStatus(active?'Referral program assigned.':'Manual enrollment removed.');
    }catch(e){setStatus(e instanceof Error?e.message:'Unable to update enrollment.');}
    finally{setBusy(false);}
  };
  const saveCode=async()=>{
    if(!referralCode||codeSaving||!targetCustomerId)return;
    setCodeSaving(true);setStatus('');
    try{
      const response=await fetch('/api/v1/staff/customers/'+
        encodeURIComponent(targetCustomerId)+'/referral-code',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,code:codeDraft,
          expectedVersion:referralCode.version}),
      });
      const result=await response.json() as {ok:boolean;
        data?:{referralCode:ReferralCode};error?:{code:string}};
      if(!response.ok||!result.ok||!result.data?.referralCode)
        throw Error(result.error?.code==='REFERRAL_CODE_TAKEN'?
          'That referral code is already in use.':
          result.error?.code==='REFERRAL_CODE_CHANGED'?
            'Code changed elsewhere. Refresh to edit again.':
            'Referral code could not be saved.');
      setReferralCode(result.data.referralCode);setCodeEditing(false);
      setStatus('Saved');
    }catch(error){setStatus(error instanceof Error?error.message:
      'Referral code could not be saved.');}
    finally{setCodeSaving(false);}
  };
  const active=grants.filter(g=>g.endedAt===null);
  const selected=active.find(g=>g.source==='MANUAL')??active[0]??null;
  const count=history?.completedCount??selected?.verifiedCompletedReferrals??0;
  const isFloatThree=selected?.programName==='Refer 3, Float for Free!';
  return <section className="ecfs-referral-profile" aria-label="Referral Programs">
    {selected?<button type="button" className="ecfs-profile-inline-link"
      aria-label="Change referral program" onClick={()=>{
        setEditing(v=>!v);setProgramId(selected.programId);
      }}>{selected.programName}</button>:
      <button type="button" className="ecfs-profile-inline-link"
        onClick={()=>setEditing(v=>!v)}>Assign a Referral Program</button>}
    {history&&history.total>0&&<button type="button"
      className="ecfs-profile-inline-link ecfs-referral-linked-customers"
      onClick={()=>setHistoryOpen(true)}>
      {history.total} referred {history.total===1?'user':'users'}
    </button>}
    {historyOpen&&history&&<div className="ecfs-profile-waiver-backdrop"
      role="presentation" onClick={()=>setHistoryOpen(false)}>
      <section className="ecfs-profile-waiver-popup" role="dialog"
        aria-modal="true" aria-label={'Referred by '+history.referrerName}
        onClick={event=>event.stopPropagation()}>
        <button type="button" className="ecfs-profile-waiver-close"
          onClick={()=>setHistoryOpen(false)}>Close</button>
        <h2>Referred by {history.referrerName}</h2>
        <table><thead><tr><th>User</th><th>Signup</th><th>First Service</th>
          </tr></thead><tbody>{history.referred.map(customer=><tr
            key={customer.customerId}>
            <td><a href={'/users/'+encodeURIComponent(customer.customerId)}>
              {customer.displayName}</a></td>
            <td>{new Date(customer.signupAt).toLocaleDateString('en-US')}</td>
            <td>{customer.completedFirstService&&customer.firstServiceAt?
              <>{new Date(customer.firstServiceAt).toLocaleString('en-US',
                {year:'numeric',month:'short',day:'numeric',
                  hour:'numeric',minute:'2-digit'})}
                {customer.firstService?' '+customer.firstService:''}</>:
              'Not completed'}</td>
          </tr>)}</tbody></table>
        {history.hasMore&&<p>Showing first 50 referred users.</p>}
      </section>
    </div>}
    {historyError&&<small role="status">{historyError}</small>}
    {isFloatThree&&<p className="ecfs-referral-progress">
      {count%3} of 3 for next free 60-Minute Float</p>}
    {selected&&referralCode?.active&&(!codeEditing?
      <button type="button" className="ecfs-profile-inline-link"
        aria-label="Edit referral code" onClick={()=>{
          setCodeDraft(referralCode.code);setCodeEditing(true);
        }}>{referralCode.code}</button>:
      <div className="ecfs-referral-code-editor">
        <input type="text" aria-label="Referral code"
          maxLength={9} value={codeDraft}
          onChange={event=>setCodeDraft(event.target.value)}
          onKeyDown={event=>{if(event.key==='Enter')void saveCode();
            if(event.key==='Escape')setCodeEditing(false);}}/>
        <button type="button" disabled={codeSaving}
          onClick={()=>void saveCode()}>{codeSaving?'Saving…':'Save'}</button>
        <button type="button" className="ecfs-profile-inline-link"
          disabled={codeSaving} onClick={()=>setCodeEditing(false)}>Cancel</button>
      </div>)}

    {editing&&<div className="ecfs-referral-program-picker">
      <select aria-label="Referral Program" value={programId}
        onChange={e=>setProgramId(e.target.value)}>
        <option value="">Choose program</option>
        {available.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {programId&&<button type="button" disabled={busy}
        onClick={()=>void change(programId,true)}>
        {busy?'Saving…':'Save'}</button>}
      {selected?.source==='MANUAL'&&
        <button type="button" disabled={busy} className="ecfs-profile-inline-link"
          onClick={()=>void change(selected.programId,false)}>Remove from program</button>}
      <button type="button" className="ecfs-profile-inline-link"
        onClick={()=>{setEditing(false);setProgramId('');}}>Cancel</button>
    </div>}
    {status&&<small role="status">{status}</small>}
  </section>;
}
